import React from 'react';
import cn from 'classnames';
import { useFieldArray, useWatch } from 'react-hook-form';
import _get from 'lodash.get';
import _set from 'lodash.set';
import deepEqual from 'fast-deep-equal';
import {
  Button,
  SelectOptionElement,
  useDidUpdateEffect,
} from '@odin-labs/components';
import { ChangeType } from 'apollo/generated/client-operations';
import { Editable } from 'types';
import { ensureNonUndefinedFields, isEmpty, usePrevious } from 'utils';
import { PlusIcon, TrashAltIcon } from 'components/icons';
import {
  FormData,
  FormInput,
  FormRepeaterArgs,
  FormRepeaterProps,
  RemoveItemPosition,
} from './types';

type ObjWithKeyAndChangeType = { internalKey: string; changeType?: ChangeType };

function isEqual(obj1: unknown, obj2: unknown): boolean {
  if (obj1 === obj2) return true;
  if (obj1 == null || obj2 == null) return false;
  const {
    // eslint-disable-next-line unused-imports/no-unused-vars
    internalKey: obj1Key,
    // eslint-disable-next-line unused-imports/no-unused-vars
    changeType: obj1ChangeType,
    ...obj1WithoutKeyAndChangeType
  } = obj1 as ObjWithKeyAndChangeType;
  const {
    // eslint-disable-next-line unused-imports/no-unused-vars
    internalKey: obj2Key,
    // eslint-disable-next-line unused-imports/no-unused-vars
    changeType: obj2ChangeType,
    ...obj2WithoutKeyAndChangeType
  } = obj2 as ObjWithKeyAndChangeType;

  return deepEqual(obj1WithoutKeyAndChangeType, obj2WithoutKeyAndChangeType);
}

type ItemContainerStyleArgs = {
  itemContainerClassName: string;
  changeType: ChangeType;
  removeItemPosition: RemoveItemPosition;
  suppressItemBorder: boolean;
};

const classes = {
  container: (className: string): string =>
    cn('odin-flex odin-flex-col odin-gap-y-9', className),
  itemsContainer: (withSeparators: boolean): string =>
    cn(
      'odin-flex odin-flex-col odin-gap-y-5',
      withSeparators && 'odin-divide-y odin-divide-gray-200',
    ),
  itemContainer: ({
    itemContainerClassName,
    changeType,
    removeItemPosition,
    suppressItemBorder,
  }: ItemContainerStyleArgs): string =>
    cn(
      'odin-flex',
      itemContainerClassName,
      changeType === ChangeType.Removed && 'odin-opacity-50',
      !suppressItemBorder &&
        'odin-border odin-border-gray-200 odin-shadow-sm odin-rounded-lg odin-p-5',
      removeItemPosition === 'top' && 'odin-flex-col odin-gap-y-2',
      removeItemPosition === 'bottom' && 'odin-flex-col-reverse odin-gap-y-5',
      removeItemPosition === 'inline' && 'odin-flex-row-reverse odin-gap-x-5',
    ),
  removeItemHolder: (removeItemClassName: string): string =>
    cn(removeItemClassName, 'odin-flex odin-justify-end odin-items-center'),
  itemFieldsContainer: (itemClassName: string): string =>
    cn('odin-w-full', itemClassName),
};

export const forwardedFormRepeater = React.forwardRef(
  <TFields extends FormData>(
    props: FormRepeaterProps<TFields>,
    ref: React.ForwardedRef<HTMLDivElement>,
  ): React.ReactElement => {
    const {
      name,
      methods,
      inputs,
      getNewItem,
      resolveElement,
      className,
      suppressItemBorder,
      withSeparators,
      itemContainerClassName,
      itemClassName,
      removeItemClassName,
      isAddItemDisabled,
      addItemText = 'Add Item',
      addItemIcon = PlusIcon,
      isRemoveItemDisabled,
      removeItemText,
      removeItemIcon = TrashAltIcon,
      removeItemPosition = 'top',
      appendType = 'prepend',
    } = props;

    const { enabled: shouldTrackChanges, idField } =
      props.shouldTrackChanges && typeof props.shouldTrackChanges === 'object'
        ? props.shouldTrackChanges
        : { enabled: props.shouldTrackChanges, idField: 'id' };

    const { fields, insert, append, remove } = useFieldArray({
      control: methods.control,
      name,
      keyName: 'internalKey',
    });

    const addItem = (): void => {
      const newItem = getNewItem(fields.length);
      if (shouldTrackChanges) {
        Object.assign(newItem, { changeType: ChangeType.Created });
      }
      if (appendType === 'prepend') {
        insert(0, newItem);
      } else {
        append(newItem);
      }
    };

    const removeItem = (index: number): void => {
      if (!shouldTrackChanges) {
        // No need to track, just remove
        remove(index);
      } else {
        const field = fields[index];
        if (!field) return;

        if (field.changeType === ChangeType.Created) {
          // New field not yet persisted — safe to remove
          remove(index);
        } else {
          // Mark as removed for tracking
          field.changeType = ChangeType.Removed;
          methods.setValue(
            `${name}[${index}].changeType` as keyof TFields,
            ChangeType.Removed as TFields[keyof TFields],
            { shouldDirty: true },
          );
        }
      }
    };

    const arrayValue = useWatch<
      Array<
        Editable<TFields> & { internalKey: string; [idField]: string | number }
      >
    >({
      name,
      control: methods.control,
    });
    const prevArrayValue = usePrevious(arrayValue);

    // This effect sets the changeType to `updated` for the updated items.
    useDidUpdateEffect(() => {
      if (!shouldTrackChanges) return;

      if (!deepEqual(arrayValue, prevArrayValue)) {
        // Check for updated items
        arrayValue.forEach((item, index) => {
          // If the item is added or removed, we don't want to set the changeType to `updated`.
          if (item.changeType) return;

          if (!item[idField]) {
            throw new Error(
              `Item is missing the required id field: ${String(idField)}`,
            );
          }

          const prevItem = prevArrayValue?.find(
            (prev) => prev[idField] === item[idField],
          );
          // After the form gets submitted, the data is reset by removing the `changeType` property for each item.
          // When an item is reset we don't want to set the changeType to `updated`.

          // In order to detect if the item has been reset, we need to check:
          const isItemReset =
            // If the previous item doesn't exist, which means that
            // the item has been initially added with a temporary id.
            // This happens when the id field changes after the entity is saved.
            // i.e. an entity.id initially is set to a temporary id using `getTempId()`,
            // and after the entity gets saved the entity.id shows up with the new guid from db
            !prevItem ||
            // Or if the previous item's `changeType` property is `created`,
            // This happens when the id field doesn't change after the entity is saved,
            // so there is no temporary id involved.
            prevItem.changeType === ChangeType.Created ||
            // or if the previous item's `changeType` property is `updated`,
            prevItem.changeType === ChangeType.Updated;

          // Set changeType to `updated` if the item has changed and is not reset.
          if (!isItemReset && !isEqual(item, prevItem)) {
            methods.setValue(
              `${name}[${index}].changeType` as keyof TFields,
              ChangeType.Updated as TFields[keyof TFields],
              { shouldDirty: true },
            );
          }
        });
      }
    }, [arrayValue]);

    // This effect resets the changeType when dirty fields are empty.
    // It happens after the user saves data and the form is reset.
    useDidUpdateEffect(() => {
      if (!shouldTrackChanges) return;

      const dirtyFields = _get(methods.formState.dirtyFields, name) as
        | Array<Partial<TFields>>
        | undefined;
      if (!dirtyFields?.length && !deepEqual(arrayValue, prevArrayValue)) {
        const defaultChangeType = fields?.[0]?.changeType;
        arrayValue.forEach((item, index) => {
          if (!item.changeType) return;
          // reset the changeType to defaultChangeType (null or undefined)
          methods.setValue(
            `${name}[${index}].changeType` as keyof TFields,
            defaultChangeType,
          );
        });
      }
    }, [arrayValue]);

    // This effect resets the `changeType` when `changeType` is the only dirty field.
    // It happens when the user reverts the changes made to an item.
    useDidUpdateEffect(() => {
      if (!shouldTrackChanges) return;

      const dirtyFields = _get(methods.formState.dirtyFields, name) as
        | Array<Partial<TFields>>
        | undefined;
      const isChangeTypeDifferentThanRemovedAndTheOnlyDirtyField =
        dirtyFields?.every(
          (df, index) =>
            Object.keys(df).length === 1 &&
            'changeType' in df &&
            arrayValue?.[index]?.changeType !== ChangeType.Removed,
        );
      if (
        (!dirtyFields?.length ||
          isChangeTypeDifferentThanRemovedAndTheOnlyDirtyField) &&
        !deepEqual(arrayValue, prevArrayValue)
      ) {
        const defaultChangeType = fields?.[0]?.changeType;
        arrayValue.forEach((item, index) => {
          if (!item.changeType) return;

          // reset the changeType to defaultChangeType (null or undefined)
          methods.setValue(
            `${name}[${index}].changeType` as keyof TFields,
            defaultChangeType,
          );
        });

        // Reset form with current values to clear dirty state.
        // This ensures isDirty becomes false when all changes are reverted
        if (methods.formState.isDirty) {
          const formDirtyFields = ensureNonUndefinedFields(
            _set({ ...methods.formState.dirtyFields }, name, undefined),
          );
          if (isEmpty(formDirtyFields)) {
            // Since changeType was the only dirty field and we just reset it,
            // the form should now match its original state
            // Use queueMicrotask to ensure setValue operations complete before reset.
            queueMicrotask(() => {
              methods.reset(
                methods.getValues() as Parameters<typeof methods.reset>[0],
              );
            });
          }
        }
      }
    }, [arrayValue]);

    const getItemFnArgs = (
      index: number,
    ): FormRepeaterArgs<
      typeof fields[number],
      ReturnType<typeof methods.getValues>
    > => {
      return {
        index,
        getItem: (): typeof fields[number] => {
          return _get(methods.getValues(), `${name}[${index}]`);
        },
        getFormValues: (): ReturnType<typeof methods.getValues> => {
          return methods.getValues();
        },
      };
    };

    const getDisabledProp = (
      item: typeof fields[number],
      child: FormInput<TFields>,
      index: number,
    ): { disabled: boolean } | null => {
      if (shouldTrackChanges) {
        if (item.changeType === ChangeType.Removed) {
          return { disabled: true };
        }

        const { disabledFn } = child.elementProps as {
          disabledFn: (
            args: FormRepeaterArgs<
              typeof fields[number],
              ReturnType<typeof methods.getValues>
            >,
          ) => boolean;
        };
        if (disabledFn) {
          const itemFnArgs = getItemFnArgs(index);
          return {
            disabled: disabledFn(itemFnArgs),
          };
        }
      }
      return null;
    };

    const getOptionsProp = (
      item: typeof fields[number],
      child: FormInput<TFields>,
      index: number,
    ): { options: SelectOptionElement[] } | null => {
      const { optionsFn } = child.elementProps as {
        optionsFn: (
          args: FormRepeaterArgs<
            typeof fields[number],
            ReturnType<typeof methods.getValues>
          >,
        ) => SelectOptionElement[];
      };
      if (optionsFn) {
        const itemFnArgs = getItemFnArgs(index);
        return { options: optionsFn(itemFnArgs) };
      }
      return null;
    };

    return (
      <div ref={ref} className={classes.container(className)}>
        {!isAddItemDisabled && (
          <Button
            theme="dashed"
            text={addItemText}
            icon={addItemIcon}
            className="odin-w-full odin-justify-center odin-h-20"
            onClick={addItem}
          />
        )}
        <div className={classes.itemsContainer(withSeparators)}>
          {fields.map((item, index) => {
            const itemId = item[idField as keyof typeof item];
            return (
              <div
                key={itemId ?? item.internalKey}
                className={classes.itemContainer({
                  itemContainerClassName,
                  changeType: item.changeType,
                  removeItemPosition,
                  suppressItemBorder,
                })}
              >
                {!isRemoveItemDisabled && (
                  <div
                    className={classes.removeItemHolder(removeItemClassName)}
                  >
                    <Button
                      theme="danger"
                      text={removeItemText}
                      size="sm"
                      icon={removeItemIcon}
                      onClick={() => removeItem(index)}
                      disabled={item.changeType === ChangeType.Removed}
                    />
                  </div>
                )}
                <div className={classes.itemFieldsContainer(itemClassName)}>
                  {
                    // support inputs as array only for now
                    (Array.isArray(inputs) ? inputs : []).map((child) => {
                      // when main inputs are configured as object then child.name would be prefixed with name
                      const childNameWithoutPrefix = child.name.replace(
                        `${name}.`,
                        '',
                      );
                      const childFullName = `${name}[${index}].${childNameWithoutPrefix}`;
                      const disabledProp = getDisabledProp(item, child, index);
                      const optionsProp = getOptionsProp(item, child, index);
                      const overriddenElementProps = (disabledProp ||
                        optionsProp) && {
                        elementProps: {
                          title: undefined as string,
                          ...child.elementProps,
                          ...disabledProp,
                          ...optionsProp,
                        },
                      };
                      return resolveElement({
                        ...child,
                        ...overriddenElementProps,
                        name: childFullName as never,
                        defaultValue: item[childNameWithoutPrefix],
                      });
                    })
                  }
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);
forwardedFormRepeater.displayName = 'FormRepeater';

type ForwardedFormRepeater = <TFields extends FormData>(
  props: FormRepeaterProps<TFields> & {
    ref?: React.ForwardedRef<HTMLDivElement>;
  },
) => ReturnType<typeof forwardedFormRepeater>;

export const FormRepeater = forwardedFormRepeater as ForwardedFormRepeater;
